import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getStudents, StudentListItem } from '../../../verticals/iqmax/api/students';
import { AppDispatch, RootState } from '../../../store/store';
import { returnToParentView, switchStudent } from '../store/contextSlice';

/**
 * The learner-context behaviour both switchers share.
 *
 * There are two switchers — the app sidebar's and the family console's — and
 * they differ only in markup: each is styled by a different sheet. What a
 * switch *does* is written once here, so the two cannot drift into one that
 * clears the server and one that does not.
 */
export function useLearnerContext() {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const activeContext = useSelector((state: RootState) => state.context.activeContext);
  const isSwitching = useSelector((state: RootState) => state.context.isLoading);
  const error = useSelector((state: RootState) => state.context.error);

  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [fetchError, setFetchError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const isParent = user?.role === 'PARENT';

  // Fetch students whenever isParent becomes true — covers the initial mount
  // AND the session-restore path where Redux is populated after a page refresh.
  useEffect(() => {
    if (!isParent) return;
    setFetchError(false);
    getStudents()
      .then(setStudents)
      .catch(() => setFetchError(true))
      .finally(() => setLoaded(true));
  }, [isParent]);

  const select = useCallback(
    (studentId: string) => {
      if (isSwitching) return;
      dispatch(switchStudent(studentId));
    },
    [dispatch, isSwitching],
  );

  /** Resolves true only once the server has confirmed the parent view. */
  const returnToParent = useCallback(async (): Promise<boolean> => {
    const result = await dispatch(returnToParentView());
    return returnToParentView.fulfilled.match(result);
  }, [dispatch]);

  return {
    isParent,
    activeContext,
    isSwitching,
    error,
    students,
    loaded,
    fetchError,
    select,
    returnToParent,
  };
}

/** Two-letter initials for a learner's avatar. */
export const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
